import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import {
  Check, ArrowLeft, Loader2, Zap, Star, Building2, Shield,
  AlertTriangle, Lock, Cpu
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { apiClient } from '@/lib/api'
import { useAuth } from '@/contexts/AuthContext'
import { SEOHead } from '@/components/SEOHead'

type PlanTier = 'freemium' | 'starter' | 'professional' | 'enterprise'
type AddonType = 'mcp'

// ── OSINT Platform Plans ──────────────────────────────────────────────────────

interface Plan {
  id: PlanTier
  name: string
  tagline: string
  price: string
  period: string
  priceNote?: string
  apiAccess: boolean
  features: string[]
  lockedIn?: string[]
  highlighted?: boolean
  cta: string
  accentColor: string
  borderColor: string
  iconColor: string
  icon: React.ReactNode
}

const PLANS: Plan[] = [
  {
    id: 'freemium',
    name: 'Free',
    tagline: 'Individual Researchers',
    price: 'Free',
    period: '',
    priceNote: '5 queries total',
    apiAccess: false,
    features: [
      'Core intelligence modules (BreachINT)',
      'Basic graph visualization',
      'Standard data sources',
      'PDF report generation',
      'Email support',
    ],
    lockedIn: ['API key generation', 'MCP integration'],
    cta: 'Current Plan',
    accentColor: 'text-gray-400',
    borderColor: 'border-gray-700',
    iconColor: 'text-gray-400 bg-gray-500/10',
    icon: <Star className="w-5 h-5" />,
  },
  {
    id: 'starter',
    name: 'Starter',
    tagline: 'Security Teams',
    price: '$499',
    period: '/mo',
    priceNote: '200 queries/mo',
    apiAccess: true,
    features: [
      'BreachINT + SOCINT + Social Stream',
      'X Investigation',
      'Advanced graph visualization',
      'Premium data sources',
      'Full REST API access + key generation',
      'MCP integration (Claude, Cursor)',
      'Priority support',
    ],
    highlighted: true,
    cta: 'Upgrade to Starter',
    accentColor: 'text-green-400',
    borderColor: 'border-green-500/50',
    iconColor: 'text-green-400 bg-green-500/10',
    icon: <Zap className="w-5 h-5" />,
  },
  {
    id: 'professional',
    name: 'Professional',
    tagline: 'Organizations',
    price: '$999',
    period: '/mo',
    priceNote: '500 queries/mo',
    apiAccess: true,
    features: [
      'All Starter features',
      'GEOINT satellite imagery',
      'Maritime & air traffic monitoring',
      'Cyber terrorism attribution',
      'AML Profiler (CDD)',
      'Playground graph pivoting',
      'Full REST API + webhook push',
      'MCP integration (all AI clients)',
      'Dedicated account manager',
    ],
    cta: 'Upgrade to Professional',
    accentColor: 'text-blue-400',
    borderColor: 'border-blue-500/50',
    iconColor: 'text-blue-400 bg-blue-500/10',
    icon: <Building2 className="w-5 h-5" />,
  },
  {
    id: 'enterprise',
    name: 'Enterprise / LEA',
    tagline: 'Law Enforcement',
    price: '$9,999',
    period: '/mo',
    priceNote: '1,000 queries/mo',
    apiAccess: true,
    features: [
      'All Professional features',
      'LEA-specific integrations',
      'Evidence chain of custody',
      'Classified data handling',
      'Counter-terrorism intelligence',
      'Cross-agency collaboration',
      'Facial recognition & WiFi intelligence',
      'MCP integration (air-gapped)',
    ],
    cta: 'Contact Sales',
    accentColor: 'text-yellow-400',
    borderColor: 'border-yellow-500/40',
    iconColor: 'text-yellow-400 bg-yellow-500/10',
    icon: <Shield className="w-5 h-5" />,
  },
]

// ── Component ─────────────────────────────────────────────────────────────────

export default function Pricing() {
  const { user } = useAuth()
  const navigate = useNavigate()
  const [loadingTier, setLoadingTier] = useState<PlanTier | null>(null)
  const [loadingAddon, setLoadingAddon] = useState<AddonType | null>(null)
  const [error, setError] = useState<string | null>(null)

  const currentTier = (user?.subscription_tier || 'freemium') as PlanTier

  const tierOrder: PlanTier[] = ['freemium', 'starter', 'professional', 'enterprise']
  const isCurrentPlan = (id: PlanTier) => id === currentTier
  const isDowngrade   = (id: PlanTier) => tierOrder.indexOf(id) < tierOrder.indexOf(currentTier)

  const handleUpgrade = async (tier: PlanTier) => {
    if (!user) { navigate('/register'); return }
    if (tier === 'enterprise') {
      window.location.href = 'mailto:support@sycek.io?subject=Enterprise%20Access%20Request'
      return
    }
    if (tier === 'freemium' || isCurrentPlan(tier) || isDowngrade(tier)) return

    setLoadingTier(tier)
    setError(null)
    try {
      const res = await apiClient.createCheckout(tier as 'starter' | 'professional')
      if (res.checkout_url) window.location.href = res.checkout_url
      else setError('Checkout URL not returned. Please try again.')
    } catch (err: any) {
      setError(err.message || 'Failed to start checkout. Please try again.')
    } finally {
      setLoadingTier(null)
    }
  }

  const handleAddon = async (addonType: AddonType) => {
    if (!user) { navigate('/register'); return }
    setLoadingAddon(addonType)
    setError(null)
    try {
      const res = await apiClient.createAddonCheckout(addonType)
      if (res.checkout_url) window.location.href = res.checkout_url
      else setError('Checkout URL not returned. Please try again.')
    } catch (err: any) {
      setError(err.message || 'Failed to start checkout. Please try again.')
    } finally {
      setLoadingAddon(null)
    }
  }

  const planCtaLabel = (plan: Plan) => {
    if (isCurrentPlan(plan.id)) return 'Current Plan'
    if (plan.id === 'freemium' || isDowngrade(plan.id)) return 'Free Tier'
    return plan.cta
  }

  return (
    <div className="min-h-screen bg-[#09090f] text-gray-100">
      <SEOHead
        title="Pricing — Sycek OSINT Intelligence Platform Plans"
        description="Flexible plans for every team — Free, Starter, Professional, and Enterprise. Access BreachINT, GEOINT, X Investigation, AML Profiler, Social Stream, and more. Start free, scale as you grow."
        keywords="OSINT platform pricing, breach intelligence pricing, OSINT subscription, security intelligence plans, BreachINT price, GEOINT price, X investigation price, AML profiler price, social listening price"
        canonical="/pricing"
        jsonLd={{
          "@type": "ItemList",
          "name": "Sycek OSINT Pricing Plans",
          "itemListElement": [
            { "@type": "ListItem", "position": 1, "name": "Free", "description": "Core OSINT modules for individual analysts" },
            { "@type": "ListItem", "position": 2, "name": "Starter", "description": "Full platform access for professional investigators" },
            { "@type": "ListItem", "position": 3, "name": "Professional", "description": "Advanced modules including AML Profiler and GEOINT" },
            { "@type": "ListItem", "position": 4, "name": "Enterprise", "description": "Custom deployments for teams and agencies" }
          ]
        }}
      />

      {/* Nav */}
      <div className="border-b border-gray-800 px-6 py-4 flex items-center gap-4">
        {user ? (
          <Link to="/app/subscription" className="inline-flex items-center gap-2 text-gray-400 hover:text-white text-sm transition-colors">
            <ArrowLeft className="w-4 h-4" />
            Back to Subscription
          </Link>
        ) : (
          <Link to="/" className="inline-flex items-center gap-2 text-gray-400 hover:text-white text-sm transition-colors">
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </Link>
        )}
        <div className="ml-auto flex items-center gap-3">
          {!user && (
            <>
              <Link to="/login"><Button variant="ghost" size="sm" className="text-gray-400 hover:text-white font-mono text-xs">Sign In</Button></Link>
              <Link to="/register"><Button size="sm" className="bg-green-500 hover:bg-green-600 text-black font-mono text-xs font-bold">Get Started</Button></Link>
            </>
          )}
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 py-16">

        {/* Error */}
        {error && (
          <div className="max-w-2xl mx-auto mb-8">
            <div className="bg-red-500/10 border border-red-500/30 rounded-lg px-4 py-3 flex items-center gap-3">
              <AlertTriangle className="w-4 h-4 text-red-400 shrink-0" />
              <p className="text-red-400 text-sm">{error}</p>
              <button onClick={() => setError(null)} className="ml-auto text-red-400/60 hover:text-red-400 text-xs">dismiss</button>
            </div>
          </div>
        )}

        {/* ══════════════════════════════════════════════════════════
            SECTION 1: OSINT Platform Plans
        ══════════════════════════════════════════════════════════ */}
        <div className="text-center mb-6">
          <Badge className="mb-5 bg-green-500/10 text-green-400 border border-green-500/30 font-mono text-[10px] tracking-widest">
            [INTELLIGENCE TIERS]
          </Badge>
          <h1 className="text-4xl md:text-5xl font-bold mb-4">
            OSINT Platform
            <br />
            <span className="text-green-400">Pricing</span>
          </h1>
          <p className="text-gray-400 max-w-xl mx-auto mb-4">
            Monthly subscriptions. Cancel any time. Paid plans include full API access for programmatic use.
          </p>
          <div className="inline-flex items-center gap-2 bg-yellow-500/5 border border-yellow-500/20 rounded-lg px-4 py-2 text-yellow-400/80 text-xs font-mono">
            <Lock className="w-3.5 h-3.5" />
            All accounts undergo identity verification before full access is granted.
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5 mb-20">
          {PLANS.map((plan) => {
            const isCurrent  = isCurrentPlan(plan.id)
            const isDown     = isDowngrade(plan.id)
            const isLoading  = loadingTier === plan.id
            const isEnterprise = plan.id === 'enterprise'

            return (
              <div
                key={plan.id}
                className={`relative flex flex-col rounded-xl border bg-[#0d0d1a] p-6 transition-all duration-200
                  ${plan.highlighted ? `${plan.borderColor} shadow-lg shadow-green-500/10` : `border-gray-800 hover:border-gray-700`}
                  ${isCurrent ? 'ring-2 ring-cyan-500/40' : ''}
                `}
              >
                {plan.highlighted && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                    <Badge className="bg-green-500/20 text-green-400 border border-green-500/40 font-mono text-[10px] tracking-widest px-3">[POPULAR]</Badge>
                  </div>
                )}
                {isCurrent && (
                  <div className="absolute -top-3 right-4">
                    <Badge className="bg-cyan-500/20 text-cyan-400 border border-cyan-500/40 font-mono text-[10px] px-2">ACTIVE</Badge>
                  </div>
                )}

                <div className={`inline-flex items-center justify-center w-10 h-10 rounded-lg mb-4 ${plan.iconColor}`}>
                  {plan.icon}
                </div>
                <div className="font-bold text-white font-mono text-lg mb-0.5">{plan.name}</div>
                <div className={`text-xs font-mono mb-4 ${plan.accentColor}`}>{plan.tagline}</div>

                <div className="mb-1">
                  <span className="text-3xl font-black text-white font-mono">{plan.price}</span>
                  {plan.period && <span className="text-gray-500 text-sm ml-1">{plan.period}</span>}
                </div>
                {plan.priceNote && <div className="text-xs text-gray-500 font-mono mb-3">{plan.priceNote}</div>}

                {/* API access indicator */}
                <div className={`flex items-center gap-1.5 text-xs mb-4 font-mono ${plan.apiAccess ? 'text-green-400' : 'text-gray-600'}`}>
                  {plan.apiAccess ? <Check className="w-3 h-3" /> : <Lock className="w-3 h-3" />}
                  {plan.apiAccess ? 'API access included' : 'No API access'}
                </div>

                <ul className="space-y-2.5 flex-1 mb-6">
                  {plan.features.map((f) => (
                    <li key={f} className="flex items-start gap-2 text-sm text-gray-300">
                      <Check className={`w-3.5 h-3.5 shrink-0 mt-0.5 ${plan.accentColor}`} />
                      {f}
                    </li>
                  ))}
                  {plan.lockedIn?.map((f) => (
                    <li key={f} className="flex items-start gap-2 text-sm text-gray-600 line-through">
                      <Check className="w-3.5 h-3.5 shrink-0 mt-0.5 text-gray-700" />
                      {f}
                    </li>
                  ))}
                </ul>

                <Button
                  className={`w-full font-mono text-xs font-bold transition-all
                    ${isCurrent
                      ? 'bg-cyan-500/10 text-cyan-400 border border-cyan-500/30 cursor-default hover:bg-cyan-500/10'
                      : plan.id === 'freemium' || isDown
                        ? 'bg-gray-800 text-gray-600 cursor-default hover:bg-gray-800'
                        : isEnterprise
                          ? 'bg-yellow-500/10 text-yellow-400 border border-yellow-500/30 hover:bg-yellow-500/20'
                          : plan.highlighted
                            ? 'bg-green-500 hover:bg-green-600 text-black shadow-lg shadow-green-500/20'
                            : 'bg-blue-500/10 text-blue-400 border border-blue-500/30 hover:bg-blue-500/20'
                    }
                  `}
                  disabled={isCurrent || plan.id === 'freemium' || isDown || isLoading}
                  onClick={() => handleUpgrade(plan.id)}
                >
                  {isLoading ? (
                    <span className="flex items-center gap-2"><Loader2 className="w-3.5 h-3.5 animate-spin" />Creating checkout...</span>
                  ) : planCtaLabel(plan)}
                </Button>
              </div>
            )
          })}
        </div>

        {/* ══════════════════════════════════════════════════════════
            SECTION 2: MCP Add-on
        ══════════════════════════════════════════════════════════ */}
        <div className="border-t border-gray-800 pt-16 mb-20">
          <div className="text-center mb-10">
            <Badge className="mb-4 bg-purple-500/20 text-purple-400 border border-purple-500/30 font-mono text-[10px] tracking-widest">
              [ADD-ON · ONE-TIME PURCHASE]
            </Badge>
            <h2 className="text-3xl font-bold mb-3">
              MCP <span className="text-purple-400">Access</span>
            </h2>
            <p className="text-gray-400 max-w-lg mx-auto text-sm">
              Connect Sycek OSINT directly to AI assistants (Claude, Cursor, Windsurf, and any MCP-compatible client)
              as a one-time purchase — no monthly subscription required.
            </p>
          </div>

          <div className="max-w-lg mx-auto">
            <div className="bg-[#0d0d1a] border border-purple-500/40 rounded-xl p-8 text-center shadow-lg shadow-purple-500/10">
              <div className="inline-flex items-center justify-center w-14 h-14 rounded-xl bg-purple-500/10 border border-purple-500/30 mb-5">
                <Cpu className="w-7 h-7 text-purple-400" />
              </div>
              <div className="text-4xl font-black text-white font-mono mb-1">$99</div>
              <div className="text-gray-500 text-sm mb-6 font-mono">one-time · lifetime access</div>
              <ul className="space-y-2.5 text-sm text-gray-300 text-left mb-8 max-w-xs mx-auto">
                {[
                  '20+ OSINT tools via MCP protocol',
                  'Works with Claude Desktop, Cursor, Windsurf',
                  'Air-gapped deployment option (Enterprise)',
                  'Same sk_... API key as REST access',
                  'Full tool documentation included',
                ].map((f) => (
                  <li key={f} className="flex items-start gap-2">
                    <Check className="w-3.5 h-3.5 text-purple-400 shrink-0 mt-0.5" />
                    {f}
                  </li>
                ))}
              </ul>
              <Button
                className="w-full bg-purple-500 hover:bg-purple-600 text-white font-mono text-sm font-bold shadow-lg shadow-purple-500/20"
                disabled={loadingAddon === 'mcp'}
                onClick={() => handleAddon('mcp')}
              >
                {loadingAddon === 'mcp'
                  ? <span className="flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" />Creating checkout...</span>
                  : user ? 'Buy MCP Access — $99' : 'Sign In to Purchase'}
              </Button>
              <p className="text-xs text-gray-600 mt-3 font-mono">
                Requires a paid OSINT plan for API key generation.
              </p>
            </div>
          </div>
        </div>

        {/* Footer note */}
        <div className="border-t border-gray-800 pt-16 grid grid-cols-1 md:grid-cols-3 gap-4 text-center">
          {[
            { title: 'Monthly Billing', body: 'Cancel any time. No lock-in. Billed monthly via DodoPayments.' },
            { title: 'Instant Activation', body: 'Tier activates immediately after payment. No manual review required.' },
            { title: 'Secure Checkout', body: 'Payments by DodoPayments. We never store your card details.' },
          ].map((item) => (
            <div key={item.title} className="bg-[#0d0d1a] border border-gray-800 rounded-xl p-4">
              <div className="text-green-400 font-mono text-[10px] font-bold mb-1.5 tracking-widest">{item.title.toUpperCase()}</div>
              <p className="text-gray-500 text-xs">{item.body}</p>
            </div>
          ))}
        </div>

        <div className="mt-8 text-center">
          <p className="text-gray-600 text-sm">
            Questions?{' '}
            <a href="mailto:support@sycek.io?subject=Pricing%20Inquiry" className="text-green-400 hover:text-green-300 underline">Contact our team</a>
          </p>
        </div>

      </div>
    </div>
  )
}
